import { UntypedFormGroup } from '@angular/forms';
import { checkIfExpression } from '@jump-tech-frontend/angular-common';
import { IfDefinition, QuestionBase } from '@jump-tech-frontend/domain';

interface QuestionComponent {
  question: QuestionBase<any>;
  form: UntypedFormGroup;

  get isInvalid(): boolean;

  get show(): boolean;

  get value(): boolean;
}

export class BaseQuestionComponent implements QuestionComponent {
  question: QuestionBase<any>;
  form: UntypedFormGroup;

  /**
   * Wider data context (typically the full project data) used only to evaluate
   * `showIf` expressions. A block/modal edit form only contains controls for the
   * fields in that block, so a `showIf` that references a field living elsewhere
   * on the project (e.g. `kitListCoreSystem`) would otherwise resolve to empty and
   * mis-evaluate — hiding and disabling a correctly-answered field. Form values
   * still take precedence so in-flight edits keep driving visibility. See JMP-6650.
   */
  showIfContext?: Record<string, unknown>;

  get isInvalid() {
    return this.form.touched && this.form.invalid;
  }

  get show() {
    return (this.question.showIfPopulated !== true || this.form.value) && this.question.show !== false;
  }

  get value() {
    return this.form?.get(this.question.key)?.value || [];
  }

  set value(value: any) {
    this.form?.get(this.question.key)?.patchValue(value);
  }

  get showIf() {
    if (!this.question.showIf) {
      return true;
    }

    const currentForm = this.form.getRawValue();
    const parentForm = this.form.parent?.getRawValue() || {};
    // showIfContext may carry the project's raw `data` object, and checkIfExpression's
    // getPropertyValue() re-spreads a context's `data` key over the top level at
    // evaluation time — which would let saved project values shadow in-flight form
    // edits (JMP-6793). Flatten it here at lowest priority instead, so form values
    // keep driving visibility.
    const { data: showIfContextData, ...showIfContextRest } = this.showIfContext ?? {};
    const context = {
      ...(showIfContextData as Record<string, unknown> | undefined),
      ...showIfContextRest,
      ...parentForm,
      ...currentForm
    };

    const show = checkIfExpression(
      this.question.showIf as IfDefinition,
      context,
      this.form?.get(this.question.key)?.value
    );

    if (!show && this.form?.get(this.question.key)?.enabled) {
      this.form?.get(this.question.key)?.disable();
    } else if (show && this.form?.get(this.question.key)?.disabled) {
      this.form?.get(this.question.key)?.enable();
    }

    return show;
  }
}
